'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  getCharacters, getLocations, getLoreNotes,
  extractBrainstormUpdates, patchCharacterFields,
  extractLocationUpdates, patchLocationFields,
  extractLoreUpdates, patchLoreFields,
} from '@/lib/actions';
import { ArrowLeft, Check, CheckSquare, Square, Sparkles, Wand2 } from 'lucide-react';

type EntityType = 'character' | 'location' | 'lore';
type Step = 'picking' | 'extracting' | 'reviewing' | 'saving' | 'saved';
interface CustomField { label: string; value: string; }
interface EntityOption { id: number; name: string; }

// ── Field config per entity type ──────────────────────────────────────────────

const CHAR_FIELDS = [
  { key: 'background',  label: 'Background',    color: 'text-purple-400' },
  { key: 'personality', label: 'Personality',   color: 'text-amber-400'  },
  { key: 'abilities',   label: 'Abilities',     color: 'text-emerald-400'},
] as const;

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  messages: { role: string; content: string }[];
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function OracleCommitPanel({ messages, onClose }: Props) {
  const router = useRouter();

  // Entity picker state
  const [entityType, setEntityType] = useState<EntityType>('character');
  const [entityId, setEntityId] = useState('');
  const [options, setOptions] = useState<Record<EntityType, EntityOption[]>>({
    character: [], location: [], lore: [],
  });
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Commit flow state
  const [step, setStep] = useState<Step>('picking');
  const [proposals, setProposals] = useState<Record<string, any>>({});
  const [edited, setEdited] = useState<Record<string, any>>({});
  const [checkedFields, setCheckedFields] = useState<Set<string>>(new Set());
  const [entityName, setEntityName] = useState('');

  // Load all entities on mount
  useEffect(() => {
    (async () => {
      const [chars, locs, lores] = await Promise.all([getCharacters(), getLocations(), getLoreNotes()]);
      setOptions({
        character: (chars as any[]).map(c => ({ id: c.id, name: c.name })),
        location:  (locs  as any[]).map(l => ({ id: l.id, name: l.name })),
        lore:      (lores as any[]).map(n => ({ id: n.id, name: n.title })),
      });
      setLoadingOptions(false);
    })();
  }, []);

  // Reset entity selection when type changes
  useEffect(() => { setEntityId(''); }, [entityType]);

  const selectedName = options[entityType].find(o => String(o.id) === entityId)?.name ?? '';

  // ── Extraction ──────────────────────────────────────────────────────────────

  const handleExtract = async () => {
    if (!entityId) return;
    setEntityName(selectedName);
    setStep('extracting');

    try {
      let result: any = {};
      if (entityType === 'character') result = await extractBrainstormUpdates(entityId, messages);
      if (entityType === 'location')  result = await extractLocationUpdates(entityId, messages);
      if (entityType === 'lore')      result = await extractLoreUpdates(entityId, messages);

      const fieldKeys = Object.keys(result).filter(k => k !== 'custom_fields' && result[k]);
      const hasContent = fieldKeys.length > 0 || (result.custom_fields?.length ?? 0) > 0;

      if (!hasContent) {
        // Nothing found — back to picking with a notice
        setStep('picking');
        return;
      }

      const checked = new Set<string>();
      fieldKeys.forEach(k => checked.add(k));
      result.custom_fields?.forEach((_: any, i: number) => checked.add(`cf_${i}`));

      setProposals(result);
      setEdited({ ...result });
      setCheckedFields(checked);
      setStep('reviewing');
    } catch {
      setStep('picking');
    }
  };

  // ── Confirm ─────────────────────────────────────────────────────────────────

  const handleConfirm = async () => {
    setStep('saving');
    const newCF = (edited.custom_fields ?? []).filter((_: any, i: number) => checkedFields.has(`cf_${i}`));

    if (entityType === 'character') {
      const patches: Parameters<typeof patchCharacterFields>[1] = {};
      if (checkedFields.has('background')  && edited.background)  patches.background  = edited.background;
      if (checkedFields.has('personality') && edited.personality) patches.personality = edited.personality;
      if (checkedFields.has('abilities')   && edited.abilities)   patches.abilities   = edited.abilities;
      if (newCF.length > 0) patches.new_custom_fields = newCF;
      await patchCharacterFields(entityId, patches);
    }
    if (entityType === 'location') {
      const patches: Parameters<typeof patchLocationFields>[1] = {};
      if (checkedFields.has('lore') && edited.lore) patches.lore = edited.lore;
      if (newCF.length > 0) patches.new_custom_fields = newCF;
      await patchLocationFields(entityId, patches);
    }
    if (entityType === 'lore') {
      const patches: Parameters<typeof patchLoreFields>[1] = {};
      if (checkedFields.has('content') && edited.content) patches.content = edited.content;
      if (newCF.length > 0) patches.new_custom_fields = newCF;
      await patchLoreFields(entityId, patches);
    }

    setStep('saved');
    setTimeout(() => { router.refresh(); onClose(); }, 1800);
  };

  const toggleField = (key: string) => setCheckedFields(prev => {
    const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next;
  });

  // ── Field list for review step ───────────────────────────────────────────────

  const mainFields: { key: string; label: string; color: string }[] =
    entityType === 'character' ? [...CHAR_FIELDS] :
    entityType === 'location'  ? [{ key: 'lore',    label: 'History & Lore', color: 'text-blue-400'    }] :
                                 [{ key: 'content',  label: 'The Record',     color: 'text-emerald-400' }];

  // ── Render ───────────────────────────────────────────────────────────────────

  if (step === 'extracting') return (
    <PanelShell onClose={onClose}>
      <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
        <div className="w-14 h-14 rounded-full bg-purple-900 border border-purple-500 flex items-center justify-center animate-pulse">
          <Sparkles size={24} className="text-purple-400" />
        </div>
        <p className="text-slate-300 font-serif italic text-lg">Reviewing your brainstorm...</p>
        <p className="text-slate-500 text-sm">Looking for ideas about {selectedName || entityName}</p>
      </div>
    </PanelShell>
  );

  if (step === 'saving') return (
    <PanelShell onClose={onClose}>
      <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
        <div className="w-14 h-14 rounded-full bg-purple-900 border border-purple-500 flex items-center justify-center animate-pulse">
          <Sparkles size={24} className="text-purple-400" />
        </div>
        <p className="text-slate-300 font-serif italic text-lg">Saving to {entityName}...</p>
      </div>
    </PanelShell>
  );

  if (step === 'saved') return (
    <PanelShell onClose={onClose}>
      <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-900/60 border border-emerald-500/50 flex items-center justify-center">
          <Check size={28} className="text-emerald-400" />
        </div>
        <p className="text-emerald-300 font-serif italic font-bold text-xl">Saved!</p>
        <p className="text-slate-500 text-sm">{entityName} has been updated.</p>
      </div>
    </PanelShell>
  );

  if (step === 'reviewing') return (
    <PanelShell onClose={onClose}>
      <div className="flex items-center gap-2 mb-1 shrink-0">
        <button onClick={() => setStep('picking')} className="p-1 text-slate-500 hover:text-slate-300 transition-colors">
          <ArrowLeft size={16} />
        </button>
        <h4 className="text-sm font-mono uppercase tracking-widest text-slate-400">
          Choose what to save to <span className="text-purple-300">{entityName}</span>
        </h4>
      </div>
      <p className="text-xs text-slate-500 italic mb-3 shrink-0">
        Edit anything, uncheck what you don't want, then confirm.
      </p>

      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {mainFields.map(({ key, label, color }) => {
          if (!proposals[key]) return null;
          const isChecked = checkedFields.has(key);
          return (
            <div key={key} className={`rounded-xl border transition-colors ${isChecked ? 'border-slate-700 bg-slate-800/50' : 'border-slate-800 bg-slate-900/30 opacity-50'}`}>
              <button onClick={() => toggleField(key)} className="w-full flex items-center gap-3 px-4 pt-3 pb-2 text-left">
                {isChecked ? <CheckSquare size={16} className={`shrink-0 ${color}`} /> : <Square size={16} className="shrink-0 text-slate-600" />}
                <span className={`text-xs font-mono uppercase tracking-widest font-bold ${color}`}>{label}</span>
              </button>
              {isChecked && (
                <div className="px-4 pb-3">
                  <textarea
                    value={edited[key] ?? ''}
                    onChange={e => setEdited(prev => ({ ...prev, [key]: e.target.value }))}
                    rows={4}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-500/50 resize-y"
                  />
                </div>
              )}
            </div>
          );
        })}

        {proposals.custom_fields?.map((cf: CustomField, i: number) => {
          const isChecked = checkedFields.has(`cf_${i}`);
          return (
            <div key={i} className={`rounded-xl border transition-colors ${isChecked ? 'border-slate-700 bg-slate-800/50' : 'border-slate-800 bg-slate-900/30 opacity-50'}`}>
              <button onClick={() => toggleField(`cf_${i}`)} className="w-full flex items-center gap-3 px-4 pt-3 pb-2 text-left">
                {isChecked ? <CheckSquare size={16} className="shrink-0 text-blue-400" /> : <Square size={16} className="shrink-0 text-slate-600" />}
                <span className="text-xs font-mono uppercase tracking-widest font-bold text-blue-400">New: {cf.label}</span>
              </button>
              {isChecked && (
                <div className="px-4 pb-3 space-y-1.5">
                  <input
                    value={edited.custom_fields?.[i]?.label ?? cf.label}
                    onChange={e => setEdited(prev => { const cfs = [...(prev.custom_fields ?? [])]; cfs[i] = { ...cfs[i], label: e.target.value }; return { ...prev, custom_fields: cfs }; })}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-300 focus:outline-none"
                  />
                  <textarea
                    value={edited.custom_fields?.[i]?.value ?? cf.value}
                    onChange={e => setEdited(prev => { const cfs = [...(prev.custom_fields ?? [])]; cfs[i] = { ...cfs[i], value: e.target.value }; return { ...prev, custom_fields: cfs }; })}
                    rows={3}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none resize-y"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 pt-4 shrink-0 border-t border-slate-800 mt-2">
        <button onClick={() => setStep('picking')} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-sm text-slate-400 transition-colors">
          Back
        </button>
        <button
          onClick={handleConfirm}
          disabled={checkedFields.size === 0}
          className="flex-1 flex items-center justify-center gap-2 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-sm font-bold transition-colors"
        >
          <Check size={16} /> Apply to {entityName}
        </button>
      </div>
    </PanelShell>
  );

  // Default: picking step
  const typeConfig: { type: EntityType; label: string; color: string }[] = [
    { type: 'character', label: 'Character', color: 'bg-purple-900/40 border-purple-500/50 text-purple-300' },
    { type: 'location',  label: 'Location',  color: 'bg-blue-900/40 border-blue-500/50 text-blue-300'     },
    { type: 'lore',      label: 'Lore Note', color: 'bg-emerald-900/40 border-emerald-500/50 text-emerald-300' },
  ];

  return (
    <PanelShell onClose={onClose}>
      <div className="mb-4 shrink-0">
        <h3 className="text-lg font-serif font-bold italic text-white mb-1">Save ideas to your world</h3>
        <p className="text-sm text-slate-400">Pick what part of the world we were building together.</p>
      </div>

      {/* Entity type tabs */}
      <div className="flex gap-2 mb-4 shrink-0">
        {typeConfig.map(({ type, label, color }) => (
          <button
            key={type}
            onClick={() => setEntityType(type)}
            className={`flex-1 py-2 rounded-lg border text-xs font-mono uppercase tracking-widest transition-all ${
              entityType === type ? color : 'border-slate-700 text-slate-500 hover:border-slate-600 hover:text-slate-400'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Entity dropdown */}
      <div className="mb-6 shrink-0">
        <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">
          Which {entityType === 'lore' ? 'lore note' : entityType}?
        </label>
        {loadingOptions ? (
          <div className="h-10 bg-slate-800 rounded-xl animate-pulse" />
        ) : (
          <select
            value={entityId}
            onChange={e => setEntityId(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-slate-200 focus:outline-none focus:border-purple-500/50"
          >
            <option value="">— Select —</option>
            {options[entityType].map(o => (
              <option key={o.id} value={String(o.id)}>{o.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex-1" />

      <div className="flex gap-2 shrink-0">
        <button onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-sm text-slate-400 transition-colors">
          Cancel
        </button>
        <button
          onClick={handleExtract}
          disabled={!entityId}
          className="flex-1 flex items-center justify-center gap-2 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-sm font-bold transition-colors"
        >
          <Wand2 size={16} /> Review what to save
        </button>
      </div>
    </PanelShell>
  );
}

// ── Shell layout ──────────────────────────────────────────────────────────────

function PanelShell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="absolute inset-0 bg-slate-950/95 backdrop-blur-sm z-20 flex flex-col">
      <div className="max-w-xl w-full mx-auto flex flex-col h-full p-8 py-10">
        {children}
      </div>
    </div>
  );
}
